import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { LoginSchema } from "@travel/shared";
import { api, ApiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/common/Button";

export function LoginPage() {
  const { user, isLoading, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: api.auth.login,
    onSuccess: async () => {
      await refresh();
      navigate(from, { replace: true });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : "로그인 실패");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-slate-500">
        불러오는 중…
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const result = LoginSchema.safeParse({ email, password });
    if (!result.success) {
      setError(result.error.issues.map((i) => i.message).join("\n"));
      return;
    }
    loginMutation.mutate(result.data);
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <h1 className="text-xl font-semibold text-slate-900">로그인</h1>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-sm text-slate-600 mb-1 block">이메일</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px]"
            />
          </label>
          <label className="block">
            <span className="text-sm text-slate-600 mb-1 block">비밀번호</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px]"
            />
          </label>
          {error && (
            <pre className="bg-rose-50 text-rose-700 text-xs rounded-xl p-3 whitespace-pre-wrap">
              {error}
            </pre>
          )}
          <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
            {loginMutation.isPending ? "로그인 중…" : "로그인"}
          </Button>
        </form>
        <p className="text-sm text-slate-500 text-center">
          계정이 없으신가요?{" "}
          <Link to="/signup" className="text-slate-900 underline">
            회원가입
          </Link>
        </p>
      </div>
    </div>
  );
}
